import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon } from 'lucide-react';
import { usePageMeta } from '../hooks/usePageMeta';
export function CookiePolicyPage() {
  usePageMeta('Cookie Policy | OnlineCollegeGuide', 'Learn how OnlineCollegeGuide.io uses cookies and similar tracking technologies.');
  return <main className="bg-white py-10 sm:py-16" data-id="element-1182" data-mp="1fdu4anyxsdw5">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-gray-800" data-id="element-1183" data-mp="1kbm6catapge2">
        <Link to="/" className="inline-flex items-center text-sm font-medium text-gray-600 hover:text-navy mb-8 transition-colors" data-id="element-1184" data-mp="1kdq8an7mw5bp">
          <ArrowLeftIcon className="w-4 h-4 mr-2" data-id="element-1185" data-mp="1uauxkvws0gwq" />
          Back to home
        </Link>

        {/* Page header */}
        <header className="border-b border-gray-200 pb-8 mb-12" data-id="element-1186" data-mp="1yac31pc7de1w">
          <h1 className="text-4xl sm:text-5xl font-bold text-navy tracking-tight leading-tight" data-id="element-1187" data-mp="3w1odxlel925s">
            Cookie Policy
          </h1>
          <p className="text-sm text-gray-500 mt-3" data-id="element-1188" data-mp="3xdsr5qi6hek">
            Last updated:{' '}
            {new Date().toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })}
          </p>
        </header>

        {/* Intro */}
        <div className="space-y-5 leading-relaxed text-base" data-id="element-1189" data-mp="31nvtx631qlh2">
          <p data-id="element-1190" data-mp="2wpkkuxkksezw">
            This Cookie Policy explains how OnlineCollegeGuide.io ("we", "us",
            or "our") uses cookies and similar tracking technologies when you
            visit our website. It explains what these technologies are, why we
            use them, and your rights to control our use of them.
          </p>
        </div>

        {/* Section 1 */}
        <NumberedSection id="section-1" number={1} title="What Are Cookies?" data-id="element-1191" data-mp="3ff9cagp7xdai">
          <Paragraph data-id="element-1192" data-mp="3fhddurzna07m">
            Cookies are small data files that are placed on your computer or
            mobile device when you visit a website. Cookies are widely used by
            website owners in order to make their websites work, or to work more
            efficiently, as well as to provide reporting information.
          </Paragraph>
          <Paragraph data-id="element-1193" data-mp="100yu4cur8mw8">
            Cookies set by the website owner (in this case,
            OnlineCollegeGuide.io) are called "first-party cookies". Cookies set
            by parties other than the website owner are called "third-party
            cookies". Third-party cookies enable third-party features or
            functionality to be provided on or through the website (e.g.,
            advertising, interactive content, and analytics).
          </Paragraph>
        </NumberedSection>

        {/* Section 2 */}
        <NumberedSection id="section-2" number={2} title="Types of Cookies We Use" data-id="element-1194" data-mp="3daq0yrj2uvo">
          <Paragraph data-id="element-1195" data-mp="88g9lqxgjxgl">
            We use first-party and third-party cookies for several reasons. Some
            cookies are required for technical reasons in order for our website
            to operate, and we refer to these as "essential" or "strictly
            necessary" cookies. Other cookies also enable us to track and target
            the interests of our users to enhance the experience on our website.
          </Paragraph>

          <SubHeading data-id="element-1196" data-mp="3kuonrz9yrqqm">Strictly Necessary Cookies</SubHeading>
          <Paragraph data-id="element-1197" data-mp="3ks28fdgu06xu">
            These cookies are essential for you to browse the website and use
            its features. Without these cookies, services like secure areas and
            basic functionality cannot be provided.
          </Paragraph>

          <SubHeading data-id="element-1198" data-mp="3psq6uk7e3q23">Performance and Analytics Cookies</SubHeading>
          <Paragraph data-id="element-1199" data-mp="7g1szpsiuk8c">
            These cookies collect information about how you use our website,
            like which pages you visited and which links you clicked on. None of
            this information can be used to identify you. It is all aggregated
            and, therefore, anonymized. Their sole purpose is to improve website
            functions.
          </Paragraph>

          <SubHeading data-id="element-1200" data-mp="25xc3ixswz39w">Functional Cookies</SubHeading>
          <Paragraph data-id="element-1201" data-mp="25wst025lfh5w">
            These cookies allow our website to remember choices you make (such
            as your user name, language, or the region you are in) and provide
            enhanced, more personal features.
          </Paragraph>

          <SubHeading data-id="element-1202" data-mp="2klyrfnwlx3cq">Advertising and Targeting Cookies</SubHeading>
          <Paragraph data-id="element-1203" data-mp="2kob5hpz8utki">
            These cookies are used to deliver advertisements more relevant to
            you and your interests. They are also used to limit the number of
            times you see an advertisement as well as help measure the
            effectiveness of the advertising campaign. They are usually placed
            by advertising networks with the website operator's permission. For
            example, we use the Meta Pixel to help us track conversions and
            deliver targeted advertisements.
          </Paragraph>
        </NumberedSection>

        {/* Section 3 */}
        <NumberedSection id="section-3" number={3} title="How Can You Control Cookies?" data-id="element-1204" data-mp="3dd08jujv18yw">
          <Paragraph data-id="element-1205" data-mp="wopkurgn3t44">
            You have the right to decide whether to accept or reject cookies.
            You can exercise your cookie rights by setting your preferences in
            your web browser.
          </Paragraph>

          <SubHeading data-id="element-1206" data-mp="mxmfveklatzk">Browser Controls</SubHeading>
          <Paragraph data-id="element-1207" data-mp="hzl0p4qabetd">
            You can set or amend your web browser controls to accept or refuse
            cookies. If you choose to reject cookies, you may still use our
            website though your access to some functionality and areas of our
            website may be restricted. As the means by which you can refuse
            cookies through your web browser controls vary from
            browser-to-browser, you should visit your browser's help menu for
            more information.
          </Paragraph>

          <SubHeading data-id="element-1208" data-mp="1ydb92nofsp8t">Opting Out of Targeted Advertising</SubHeading>
          <Paragraph data-id="element-1209" data-mp="1yc04wmr2xfh6">
            If you want to opt out of targeted advertising, you can visit the
            Digital Advertising Alliance (DAA) at{' '}
            <a href="https://optout.aboutads.info" target="_blank" rel="noopener noreferrer" className="text-navy underline hover:text-crimson" data-id="element-1210" data-mp="1g5frv5n4q7s">
              optout.aboutads.info
            </a>{' '}
            or the Network Advertising Initiative (NAI) at{' '}
            <a href="https://optout.networkadvertising.org" target="_blank" rel="noopener noreferrer" className="text-navy underline hover:text-crimson" data-id="element-1211" data-mp="fd29c4d58lsd">
              optout.networkadvertising.org
            </a>
            . You can also use the Do Not Track (DNT) settings in your browser.
          </Paragraph>
        </NumberedSection>

        {/* Section 4 */}
        <NumberedSection id="section-4" number={4} title="More Information" data-id="element-1212" data-mp="2v6sr85egdh91">
          <Paragraph data-id="element-1213" data-mp="1d8nxmru8hswg">
            For more information about how we protect your privacy, please
            review our{' '}
            <Link to="/privacy-policy" className="text-navy underline hover:text-crimson" data-id="element-1214" data-mp="18acqiovbpwhx">
              Privacy Policy
            </Link>
            . If you are a California resident looking to exercise your rights
            regarding the sale or sharing of your personal information, please
            visit our{' '}
            <Link to="/do-not-sell-my-info" className="text-navy underline hover:text-crimson" data-id="element-1215" data-mp="ufsjl64iwzgq">
              Do Not Sell My Info
            </Link>{' '}
            page.
          </Paragraph>
        </NumberedSection>

        {/* Section 5 */}
        <NumberedSection id="section-5" number={5} title="Contact Us" data-id="element-1216" data-mp="yhni62v2kyqt">
          <Paragraph data-id="element-1217" data-mp="asupqgdnp90y">
            If you have any questions about our use of cookies or other
            technologies, please contact us at:
          </Paragraph>
          <div className="mt-5 p-6 bg-gray-50 border border-gray-200 rounded-xl" data-id="element-1218" data-mp="vnwgdexkddfz">
            <p className="font-bold text-navy text-lg" data-id="element-1219" data-mp="10mg67qfa39dm">Ironsmith Media</p>
            <p className="text-gray-700 mt-2" data-id="element-1220" data-mp="10ok6lydyxq95">
              1612 UPAS Street, San Diego, CA 92103
            </p>
            <p className="text-gray-700 mt-2" data-id="element-1221" data-mp="14qe11oxkly0v">
              Email:{' '}
              <a href="mailto:contact@onlinecollegeguide.io" className="text-navy underline hover:text-crimson font-medium" data-id="element-1222" data-mp="19ofm2fek47ez">
                contact@onlinecollegeguide.io
              </a>
            </p>
          </div>
        </NumberedSection>
      </div>
    </main>;
}
/* ---------- Sub-components ---------- */
function NumberedSection({
  id,
  number,
  title,
  children
}: {
  id: string;
  number: number;
  title: string;
  children: React.ReactNode;
}) {
  return <section id={id} className="mt-16 pt-10 border-t border-gray-200 scroll-mt-8" data-id="element-1223" data-mp="1xavw5nmavkr9">
      <div className="flex items-baseline gap-3 mb-6" data-id="element-1224" data-mp="229gtnfa63qc0">
        <span className="text-crimson font-bold text-xl" data-id="element-1225" data-mp="2cgjimcq6rv05">{number}.</span>
        <h2 className="text-2xl sm:text-3xl font-bold text-navy tracking-tight leading-tight" data-id="element-1226" data-mp="27g45nm9tgpip">
          {title}
        </h2>
      </div>
      <div className="space-y-5 leading-relaxed text-gray-700 text-base" data-id="element-1227" data-mp="22i2lsz4kgm66">
        {children}
      </div>
    </section>;
}
function SubHeading({
  children
}: {
  children: React.ReactNode;
}) {
  return <h3 className="text-lg font-bold text-navy mt-8 mb-3" data-id="element-1228" data-mp="2v5q4u9q6v4qp">{children}</h3>;
}
function Paragraph({
  children
}: {
  children: React.ReactNode;
}) {
  return <p className="text-gray-700 leading-relaxed" data-id="element-1229" data-mp="2lgeukjrkfu18">{children}</p>;
}